import { useState } from 'react';
import { Box, Typography, Modal, IconButton } from '@mui/material';
import { IconX } from '@tabler/icons';
import moment from 'moment';
import i18n from 'src/utils/i18n';
import { ChatMessage } from 'src/types/apps/messages';

interface MessageBubbleProps {
  message: ChatMessage;
  isOwn: boolean;
  showSenderName?: boolean;
  senderLabel?: string;
}

// Alignment is flex-start/flex-end only (no hardcoded left/right) so it
// mirrors automatically inside the app's RTL container (plan §4 Phase 2).
const MessageBubble = ({ message, isOwn, showSenderName, senderLabel }: MessageBubbleProps) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const isImage = message.type === 'image' && !!message.imageUrl;

  return (
    <Box sx={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start' }}>
      <Box
        sx={{
          maxWidth: { xs: '74%', md: '52%' },
          padding: isImage ? '6px' : '6px 12px',
          borderRadius: '14px',
          fontSize: '13.5px',
          lineHeight: 1.35,
          backgroundColor: isOwn ? '#84BD00' : '#EFF1F4',
          color: isOwn ? '#fff' : 'inherit',
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
        }}
      >
        {showSenderName && (senderLabel || message.user.name) && (
          <Typography
            sx={{
              fontSize: '11.5px',
              fontWeight: 700,
              color: '#6FA323',
              marginBottom: '4px',
            }}
          >
            {senderLabel || message.user.name}
          </Typography>
        )}
        {isImage && (
          <Box
            component="img"
            src={message.imageUrl}
            alt=""
            onClick={() => setPreviewOpen(true)}
            sx={{
              display: 'block',
              width: '100%',
              maxWidth: 240,
              maxHeight: 240,
              objectFit: 'cover',
              borderRadius: '10px',
              cursor: 'zoom-in',
            }}
          />
        )}
        {message.text && (
          <Box sx={isImage ? { padding: '7px 8px 2px' } : undefined}>{message.text}</Box>
        )}
        <Typography
          component="div"
          sx={{
            fontSize: '10.5px',
            marginTop: '2px',
            padding: isImage ? '0 8px 4px' : 0,
            textAlign: 'right',
            opacity: isOwn ? 0.85 : 1,
            color: isOwn ? 'inherit' : '#8B95A1',
          }}
        >
          {moment(message.createdAt).locale(i18n.language).format('hh:mm A')}
        </Typography>
      </Box>

      {isImage && (
        <Modal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Box sx={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh', outline: 'none' }}>
            <IconButton
              onClick={() => setPreviewOpen(false)}
              sx={{
                position: 'absolute',
                top: -44,
                right: 0,
                color: '#fff',
                backgroundColor: 'rgba(0,0,0,0.4)',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
              }}
            >
              <IconX size={20} />
            </IconButton>
            <Box
              component="img"
              src={message.imageUrl}
              alt=""
              sx={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                borderRadius: '4px',
                display: 'block',
              }}
            />
          </Box>
        </Modal>
      )}
    </Box>
  );
};

export default MessageBubble;
